import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'

import { Table } from '#components/Table'

export interface ProfinderCarrierStatItem {
  carrierCode: string
  totalRequests: number
  uniqueLoads: number
  prosFound: number
  uniqueProsFound: number
}

type Row = ProfinderCarrierStatItem & {
  isTotal?: boolean
  successRate: number | null
}

const computeSuccessRate = (uniqueLoads: number, uniqueProsFound: number) =>
  uniqueLoads > 0 ? (uniqueProsFound / uniqueLoads) * 100 : null

const columnHelper = createColumnHelper<Row>()

const columns = [
  columnHelper.accessor('carrierCode', {
    header: 'Carrier',
    cell: info => info.getValue().toUpperCase(),
    enableSorting: true,
  }),
  columnHelper.accessor('uniqueProsFound', {
    header: 'Unique Pros Found',
    cell: info => info.getValue().toLocaleString(),
    enableSorting: true,
  }),
  columnHelper.accessor('uniqueLoads', {
    header: 'Unique Loads',
    cell: info => info.getValue().toLocaleString(),
    enableSorting: true,
  }),
  columnHelper.accessor('successRate', {
    header: 'Success %',
    cell: info => {
      const v = info.getValue()
      return v === null ? '—' : `${v.toFixed(1)}%`
    },
    enableSorting: true,
  }),
  columnHelper.accessor('prosFound', {
    header: 'Pros Found',
    cell: info => info.getValue().toLocaleString(),
    enableSorting: true,
  }),
  columnHelper.accessor('totalRequests', {
    header: 'Total Requests',
    cell: info => info.getValue().toLocaleString(),
    enableSorting: true,
  }),
]

type SortDir = 'asc' | 'desc'

interface ProFinderCarrierBreakdownProps {
  byCarrier: ProfinderCarrierStatItem[]
  totals: Omit<ProfinderCarrierStatItem, 'carrierCode'>
}

const ProFinderCarrierBreakdown = ({
  byCarrier,
  totals,
}: ProFinderCarrierBreakdownProps) => {
  const [sortKey, setSortKey] = useState<keyof Row>('totalRequests')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const toRow = (item: ProfinderCarrierStatItem): Row => ({
    ...item,
    successRate: computeSuccessRate(item.uniqueLoads, item.uniqueProsFound),
  })

  const sorted = [...byCarrier.map(toRow)].sort((a, b) => {
    const av = a[sortKey]
    const bv = b[sortKey]
    if (av === bv) return 0
    if (av === null) return 1
    if (bv === null) return -1
    const cmp = av < bv ? -1 : 1
    return sortDir === 'asc' ? cmp : -cmp
  })

  const totalsRow: Row = {
    carrierCode: 'Total',
    ...totals,
    isTotal: true,
    successRate: computeSuccessRate(totals.uniqueLoads, totals.uniqueProsFound),
  }

  const handleSortChange = (columnId: string) => {
    const key = columnId as keyof Row
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  return (
    <Table
      data={[...sorted, totalsRow]}
      columns={columns}
      getRowId={r => r.carrierCode}
      className="w-full"
      manualSorting
      onSortChange={handleSortChange}
      getSortState={columnId => (columnId === sortKey ? sortDir : false)}
    />
  )
}

export { ProFinderCarrierBreakdown }
